// Tiny DOM builder. Text always goes through text nodes: nothing a peer sends can become markup.

type Child = Node | string | number | null | undefined | false;
type Props = Record<string, unknown> & { class?: string; text?: string | number };

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, props?: Props | null, ...children: Child[]): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(props ?? {})) {
    if (value == null || value === false) continue;
    if (key === 'class') node.className = String(value);
    else if (key === 'text') node.textContent = String(value);
    else if (key.startsWith('on') && typeof value === 'function') node.addEventListener(key.slice(2), value as EventListener);
    else if (key in node && typeof value !== 'string') (node as unknown as Record<string, unknown>)[key] = value;
    else node.setAttribute(key, value === true ? '' : String(value));
  }
  append(node, children);
  return node;
}

export function append(node: Node, children: Child[]): void {
  for (const c of children) {
    if (c == null || c === false) continue;
    node.appendChild(typeof c === 'string' || typeof c === 'number' ? document.createTextNode(String(c)) : c);
  }
}

/** Optional children written as `cond && node` collapse to nothing. */
export type Maybe = Node | string | null | undefined | false;

export function keep(...items: Maybe[]): (Node | string)[] {
  return items.filter((i): i is Node | string => i != null && i !== false && i !== '');
}

export function progressBar(fraction: number): HTMLElement {
  const fill = h('div', { class: 'bar-fill' });
  fill.style.width = `${Math.max(0, Math.min(1, fraction)) * 100}%`;
  return h('div', { class: 'bar', role: 'progressbar', 'aria-valuenow': Math.round(fraction * 100) }, fill);
}
